import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarRange,
  Loader2,
  Sparkles,
} from "lucide-react";
import { collection, collectionGroup, getDocs } from "firebase/firestore";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { db } from "../../firebase";

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const toInputValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseInputDate = (value) => {
  if (!value || typeof value !== "string") return new Date(value || Date.now());
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return new Date(value);
  return new Date(year, month - 1, day);
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  return null;
};

const formatWhen = (value) => {
  const date = toJsDate(value);
  if (Number.isNaN(date.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getRowTimeMs = (row) => {
  const date = toJsDate(row?.changedAt) || new Date(0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const buildPriceChangeFromData = ({ id, data, inventoryId, inventoryMap }) => {
  const item = inventoryMap.get(String(inventoryId)) || {};
  const rawPriceChange =
    (data?.from !== undefined || data?.to !== undefined
      ? {
          from: data.from,
          to: data.to,
        }
      : null) ||
    data?.priceChange ||
    (data?.changes?.price
      ? {
          from: data.changes.price.from,
          to: data.changes.price.to,
        }
      : null);

  const from = Number(rawPriceChange?.from);
  const to = Number(rawPriceChange?.to);
  if (
    !rawPriceChange ||
    !Number.isFinite(from) ||
    !Number.isFinite(to) ||
    from <= 0 ||
    from === to
  ) {
    return null;
  }

  const delta = to - from;
  const percent = from !== 0 ? (delta / from) * 100 : null;

  return {
    id,
    inventoryId,
    name: item?.name || data?.name || item?.sku || data?.sku || "Unknown item",
    sku: item?.sku || data?.sku || "",
    category: item?.category || data?.category || "",
    changedAt: data?.changedAt || null,
    changedBy: data?.changedByLabel || data?.changedBy || "",
    type: data?.type || "updated",
    from,
    to,
    delta,
    percent,
  };
};

const mergePriceChangeRows = (rows) => {
  const sorted = [...rows].filter(Boolean).sort((a, b) => getRowTimeMs(b) - getRowTimeMs(a));
  return sorted.reduce((acc, row) => {
    const rowTime = getRowTimeMs(row);
    const duplicate = acc.find(
      (existing) =>
        existing.inventoryId === row.inventoryId &&
        Number(existing.from) === Number(row.from) &&
        Number(existing.to) === Number(row.to) &&
        Math.abs(getRowTimeMs(existing) - rowTime) < 10000
    );

    if (!duplicate) acc.push(row);
    return acc;
  }, []);
};

export default function InventoryPriceChanges({ companyId, inventory = [], sampleOrders = [] }) {
  const today = useMemo(() => new Date(), []);
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d;
  }, []);

  const [startDate, setStartDate] = useState(toInputValue(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(toInputValue(today));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  const inventoryMap = useMemo(
    () =>
      new Map(
        (Array.isArray(inventory) ? inventory : []).map((item) => [String(item.id), item])
      ),
    [inventory]
  );

  const loadPriceChanges = useCallback(async () => {
    if (!companyId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const start = startOfDay(parseInputDate(startDate));
      const end = endOfDay(parseInputDate(endDate));
      const feedSnap = await getDocs(collection(db, "companies", companyId, "inventoryPriceChanges"));

      let changelogRows = [];
      try {
        const changelogSnap = await getDocs(collectionGroup(db, "changelog"));
        changelogRows = changelogSnap.docs
          .filter((docSnap) => {
            const path = docSnap.ref.path || "";
            if (!path.includes(`companies/${companyId}/inventory/`)) return false;
            const data = docSnap.data() || {};
            return Boolean(data?.priceChange || data?.changes?.price);
          })
          .map((docSnap) => {
            const pathParts = String(docSnap.ref.path || "").split("/");
            const inventoryId = pathParts[pathParts.indexOf("inventory") + 1] || "";
            return buildPriceChangeFromData({
              id: `changelog-${docSnap.id}`,
              data: docSnap.data(),
              inventoryId,
              inventoryMap,
            });
          });
      } catch (changelogErr) {
        if (changelogErr?.code !== "permission-denied") {
          throw changelogErr;
        }
      }

      const builtRows = [
        ...feedSnap.docs.map((docSnap) =>
          buildPriceChangeFromData({
            id: docSnap.id,
            data: docSnap.data(),
            inventoryId: docSnap.data()?.inventoryId || "",
            inventoryMap,
          })
        ),
        ...changelogRows,
      ];

      const nextRows = mergePriceChangeRows(
        builtRows
          .filter((row) => {
            const time = getRowTimeMs(row);
            return time >= start.getTime() && time <= end.getTime();
          })
          .filter(Boolean)
      );

      setRows(nextRows);
    } catch (err) {
      console.error("[InventoryPriceChanges] load failed:", err);
      setRows([]);
      setError("Could not load price changes for this date range.");
    } finally {
      setLoading(false);
    }
  }, [companyId, endDate, inventory, inventoryMap, startDate]);

  useEffect(() => {
    loadPriceChanges();
  }, [loadPriceChanges, reloadKey]);

  useEffect(() => {
    const handleLiveInsert = (event) => {
      const detail = event?.detail || {};
      if (!companyId || detail.companyId !== companyId) return;

      const changedAtValue = detail.changedAt?.toDate
        ? detail.changedAt.toDate()
        : new Date(detail.changedAt || Date.now());
      if (Number.isNaN(changedAtValue.getTime())) return;

      const start = startOfDay(parseInputDate(startDate));
      const end = endOfDay(parseInputDate(endDate));
      if (changedAtValue < start || changedAtValue > end) return;

      const from = Number(detail.from);
      const to = Number(detail.to);
      if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return;

      setRows((prev) =>
        mergePriceChangeRows([
          {
            id: `local-${detail.inventoryId}-${changedAtValue.getTime()}`,
            inventoryId: detail.inventoryId,
            name: detail.name || "Unknown item",
            sku: detail.sku || "",
            category: detail.category || "",
            changedAt: detail.changedAt,
            changedBy: detail.changedByLabel || detail.changedBy || "",
            type: detail.type || "updated",
            from,
            to,
            delta: to - from,
            percent: from !== 0 ? ((to - from) / from) * 100 : null,
          },
          ...prev,
        ])
      );
    };

    window.addEventListener("inventory-price-change", handleLiveInsert);
    return () => window.removeEventListener("inventory-price-change", handleLiveInsert);
  }, [companyId, endDate, startDate]);

  const summary = useMemo(() => {
    const increases = rows.filter((row) => row.delta > 0);
    const decreases = rows.filter((row) => row.delta < 0);
    const touchedItemIds = new Set(rows.map((row) => row.inventoryId).filter(Boolean));

    return {
      totalChanges: rows.length,
      touchedItems: touchedItemIds.size,
      increases: increases.length,
      decreases: decreases.length,
      avgIncrease:
        increases.length > 0
          ? increases.reduce((sum, row) => sum + row.delta, 0) / increases.length
          : 0,
      avgDecrease:
        decreases.length > 0
          ? decreases.reduce((sum, row) => sum + Math.abs(row.delta), 0) / decreases.length
          : 0,
    };
  }, [rows]);

  const itemInsights = useMemo(() => {
    const map = new Map();

    const getItemsFromOrder = (order) => {
      if (Array.isArray(order?.lineItems) && order.lineItems.length) return order.lineItems;
      if (Array.isArray(order?.sampleItems) && order.sampleItems.length) return order.sampleItems;
      if (Array.isArray(order?.items) && order.items.length) return order.items;
      if (order?.productId) {
        return [
          {
            productId: order.productId,
            quantity: Number(order.quantity) || 1,
          },
        ];
      }
      return [];
    };

    (Array.isArray(sampleOrders) ? sampleOrders : []).forEach((order) => {
      const territoryLabel =
        order?.territoryName || order?.territory || order?.territoryId || "Unassigned territory";
      const items = getItemsFromOrder(order);

      items.forEach((item) => {
        const productId = String(
          item?.productId || item?.inventoryId || item?.itemId || item?.id || ""
        ).trim();
        const quantity = Number(item?.quantity || item?.qty || 0) || 0;
        if (!productId || quantity <= 0) return;

        const existing = map.get(productId) || {
          sampleQty: 0,
          sampleOrders: 0,
          territories: new Map(),
        };

        existing.sampleQty += quantity;
        existing.sampleOrders += 1;
        existing.territories.set(
          territoryLabel,
          (existing.territories.get(territoryLabel) || 0) + quantity
        );
        map.set(productId, existing);
      });
    });

    return map;
  }, [sampleOrders]);

  const chartRows = useMemo(() => {
    const byItem = new Map();

    rows.forEach((row) => {
      const key = row.inventoryId || row.id;
      const insight = itemInsights.get(String(row.inventoryId || ""));
      const rankedTerritories = Array.from(insight?.territories?.entries?.() || []).sort(
        (a, b) => b[1] - a[1]
      );
      const existing = byItem.get(key) || {
        inventoryId: row.inventoryId,
        name: row.name,
        sku: row.sku,
        increase: 0,
        decrease: 0,
        count: 0,
        latestAt: 0,
        sampleQty: insight?.sampleQty || 0,
        sampleOrders: insight?.sampleOrders || 0,
        topTerritory: rankedTerritories[0]?.[0] || "",
      };

      if (row.delta > 0) existing.increase += row.delta;
      if (row.delta < 0) existing.decrease += Math.abs(row.delta);
      existing.count += 1;
      existing.latestAt = Math.max(existing.latestAt, getRowTimeMs(row));
      existing.name = row.name || existing.name;
      existing.sku = row.sku || existing.sku;
      byItem.set(key, existing);
    });

    return Array.from(byItem.values())
      .map((item) => ({
        ...item,
        label:
          item.name && item.name.length > 26
            ? `${item.name.slice(0, 26)}...`
            : item.name || item.sku || "Unknown item",
        totalMovement: item.increase + item.decrease,
        exposureValue: (item.increase + item.decrease) * Math.max(item.sampleQty || 0, 1),
      }))
      .sort((a, b) => b.exposureValue - a.exposureValue || b.latestAt - a.latestAt)
      .slice(0, 8);
  }, [itemInsights, rows]);

  const territoryExposureRows = useMemo(() => {
    const byTerritory = new Map();

    rows.forEach((row) => {
      const insight = itemInsights.get(String(row.inventoryId || ""));
      const territories = Array.from(insight?.territories?.entries?.() || []);
      if (!territories.length) return;

      territories.forEach(([territoryName, sampleQty]) => {
        const entry = byTerritory.get(territoryName) || {
          territoryName,
          sampledUnits: 0,
          gain: 0,
          loss: 0,
        };
        const weightedChange = Math.abs(Number(row.delta || 0)) * Number(sampleQty || 0);
        entry.sampledUnits += Number(sampleQty || 0);
        if (row.delta > 0) entry.gain += weightedChange;
        if (row.delta < 0) entry.loss += weightedChange;
        byTerritory.set(territoryName, entry);
      });
    });

    return Array.from(byTerritory.values())
      .map((entry) => ({
        ...entry,
        net: entry.gain - entry.loss,
      }))
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net) || b.sampledUnits - a.sampledUnits)
      .slice(0, 4);
  }, [itemInsights, rows]);

  const chartTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    return (
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {row.sku ? <div className="text-xs text-slate-500">SKU {row.sku}</div> : null}
        <div className="mt-2 space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span className="text-emerald-700">Total increases</span>
            <span className="font-medium text-emerald-900">{formatMoney(row.increase)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-rose-700">Total decreases</span>
            <span className="font-medium text-rose-900">{formatMoney(row.decrease)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-slate-500">Change events</span>
            <span className="font-medium text-slate-800">{row.count}</span>
          </div>
        </div>
      </div>
    );
  }, []);

  const presetButton = (label, days) => (
    <button
      key={label}
      type="button"
      onClick={() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - (days - 1));
        setStartDate(toInputValue(start));
        setEndDate(toInputValue(end));
      }}
      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-[20px] border border-slate-200 bg-gradient-to-br from-white via-sky-50 to-slate-50 p-3 shadow-sm">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Price Changes
                </div>
                <div className="text-sm font-semibold text-slate-900">Inventory Pricing History</div>
              </div>
              <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-600">
                <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1">
                  {summary.totalChanges} changes
                </span>
                <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1">
                  {summary.touchedItems} items
                </span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                  {summary.increases} up
                </span>
                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700">
                  {summary.decreases} down
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
                <CalendarRange className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-slate-500">From</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                />
                <span className="text-slate-400">To</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                />
              </div>
              {[
                ["7D", 7],
                ["30D", 30],
                ["90D", 90],
              ].map(([label, days]) => presetButton(label, days))}
              <button
                type="button"
                onClick={() => setReloadKey((prev) => prev + 1)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white/75 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Territory Exposure
            </div>
            {territoryExposureRows.length === 0 ? (
              <div className="mt-2 text-xs text-slate-500">
                No sampled territory exposure yet in this date range.
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                {territoryExposureRows.map((territory) => (
                  <div key={territory.territoryName} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {territory.territoryName}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {territory.sampledUnits} sampled units tied to moved items
                      </div>
                    </div>
                    <div
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        territory.net >= 0
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-rose-100 text-rose-800"
                      }`}
                    >
                      {territory.net >= 0 ? "+" : "-"}
                      {formatMoney(Math.abs(territory.net))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Top Price Movers</div>
              <div className="text-xs text-slate-500">
                Total increases vs decreases by item in the selected range.
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex h-[420px] items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading price changes...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-700">
              {error}
            </div>
          ) : chartRows.length === 0 ? (
            <div className="flex h-[420px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 text-center">
              <div>
                <div className="text-sm font-medium text-slate-700">No chart data in this range</div>
                <div className="mt-1 text-sm text-slate-500">
                  Change a few prices or widen the date range to see movement.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-emerald-50 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700">Total Up</div>
                  <div className="mt-1 text-lg font-semibold text-emerald-900">
                    {formatMoney(chartRows.reduce((sum, row) => sum + row.increase, 0))}
                  </div>
                </div>
                <div className="rounded-2xl bg-rose-50 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-rose-700">Total Down</div>
                  <div className="mt-1 text-lg font-semibold text-rose-900">
                    {formatMoney(chartRows.reduce((sum, row) => sum + row.decrease, 0))}
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Most Active SKU</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {chartRows[0]?.sku ? `SKU ${chartRows[0].sku}` : chartRows[0]?.label || "—"}
                  </div>
                </div>
              </div>

              <div className="h-[360px] rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartRows}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    barCategoryGap={14}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#dbe4f0" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      tickFormatter={(value) => `$${Number(value).toFixed(2)}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={150}
                      tick={{ fill: "#475569", fontSize: 12 }}
                    />
                    <Tooltip content={chartTooltip} cursor={{ fill: "rgba(148,163,184,0.12)" }} />
                    <Bar dataKey="increase" radius={[0, 8, 8, 0]} fill="#22c55e" name="Increases">
                      {chartRows.map((row) => (
                        <Cell key={`${row.inventoryId}-increase`} fill="#34d399" />
                      ))}
                    </Bar>
                    <Bar dataKey="decrease" radius={[0, 8, 8, 0]} fill="#fb7185" name="Decreases">
                      {chartRows.map((row) => (
                        <Cell key={`${row.inventoryId}-decrease`} fill="#fb7185" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Showing {rows.length} price change{rows.length === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-slate-500">
                Every saved inventory price move inside your selected date range.
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading price changes...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-700">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
              <div className="text-sm font-medium text-slate-700">No price changes in this range</div>
              <div className="mt-1 text-sm text-slate-500">
                Try a wider date range to see historical pricing movement.
              </div>
            </div>
          ) : (
            <div className="max-h-[680px] space-y-3 overflow-y-auto pr-1">
              {rows.map((row) => {
                const increase = row.delta > 0;
                return (
                  <div
                    key={row.id}
                    className={`rounded-2xl border px-4 py-3 shadow-sm transition ${
                      increase
                        ? "border-emerald-200 bg-emerald-50/60"
                        : "border-rose-200 bg-rose-50/60"
                    }`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate text-sm font-semibold text-slate-900">
                            {row.name}
                          </div>
                          {row.category ? (
                            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              {row.category}
                            </span>
                          ) : null}
                          {row.sku ? (
                            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500">
                              SKU {row.sku}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {formatWhen(row.changedAt)}
                          {row.changedBy ? ` • ${row.changedBy}` : ""}
                        </div>
                        {(() => {
                          const insight = itemInsights.get(String(row.inventoryId || ""));
                          const rankedTerritories = Array.from(
                            insight?.territories?.entries?.() || []
                          ).sort((a, b) => b[1] - a[1]);
                          return insight ? (
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                              <span className="rounded-full bg-white px-2 py-1 font-medium text-slate-600">
                                {insight.sampleOrders} samples
                              </span>
                              <span className="rounded-full bg-white px-2 py-1 font-medium text-slate-600">
                                {insight.sampleQty} units sampled
                              </span>
                              {rankedTerritories[0]?.[0] ? (
                                <span className="rounded-full bg-white px-2 py-1 font-medium text-slate-600">
                                  Top territory: {rankedTerritories[0][0]}
                                </span>
                              ) : null}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                          <div className="text-[11px] uppercase tracking-wide text-slate-400">From</div>
                          <div className="font-semibold text-slate-700">{formatMoney(row.from)}</div>
                        </div>
                        <div className="rounded-xl bg-white px-3 py-2 shadow-sm">
                          <div className="text-[11px] uppercase tracking-wide text-slate-400">To</div>
                          <div className="font-semibold text-slate-900">{formatMoney(row.to)}</div>
                        </div>
                        <div
                          className={`rounded-xl px-3 py-2 shadow-sm ${
                            increase ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          <div className="text-[11px] uppercase tracking-wide opacity-70">Change</div>
                          <div className="font-semibold">
                            {increase ? "+" : "-"}
                            {formatMoney(Math.abs(row.delta))}
                            {row.percent != null ? ` (${increase ? "+" : ""}${row.percent.toFixed(1)}%)` : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
